import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /apec — APEC Business Travel Card (ABTC) landing page
import { createFileRoute } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE_AP = "https://nyclanguageinstitute.com";

const TITLE = "APEC Business Travel Card (ABTC) — รับยื่นครบวงจร | NYC";
const DESC = "บริการขอ APEC Business Travel Card (ABTC) สำหรับนักธุรกิจไทย เดินทาง 21 economies ฟรีวีซ่า ใช้ช่อง APEC Lane ตรวจคนเข้าเมือง — ส่งภาพเอกสารมาเช็กให้ก่อน ฟรี ไม่คิดค่าปรึกษา";

const steps = [
  { name: "ปรึกษาฟรี + ตรวจคุณสมบัติ", text: "ทีมตรวจคุณสมบัตินักธุรกิจ (กรรมการ/ผู้บริหาร/ผู้ถือหุ้น ≥ 5%) และจัดทำแผนเอกสารตามแนวทาง MFA Thailand" },
  { name: "เตรียมเอกสาร", text: "หนังสือรับรองบริษัท (ไม่เกิน 6 เดือน), หนังสือเดินทาง, รูปถ่าย 2 นิ้ว, แบบฟอร์ม APEC-1, จดหมายธุรกิจอธิบายความจำเป็นเดินทาง" },
  { name: "ยื่นกรมการกงสุล MFA", text: "ทีมยื่นคำขอ ณ กองหนังสือเดินทาง MFA แจ้งวัฒนะ พร้อมตรวจประวัติเบื้องต้น" },
  { name: "ตรวจสอบ pre-clearance 21 economies", text: "ติดตามผล pre-clearance จากทุกประเทศสมาชิก APEC (3-4 เดือน) ผ่านระบบ MFA" },
  { name: "รับบัตร ABTC", text: "บัตรอายุ 5 ปี ส่ง EMS ฟรีทั่วไทย พร้อมคู่มือการใช้ APEC Lane ที่สนามบินทั่ว 21 economies" },
];

const faqs = [
  { q: "APEC Card ใช้ได้กี่ประเทศ?", a: "ใช้ได้ 21 economies: ออสเตรเลีย, บรูไน, แคนาดา, ชิลี, จีน, ฮ่องกง, อินโดนีเซีย, ญี่ปุ่น, เกาหลี, มาเลเซีย, เม็กซิโก, นิวซีแลนด์, ปาปัวนิวกินี, เปรู, ฟิลิปปินส์, รัสเซีย, สิงคโปร์, ไต้หวัน, ไทย, สหรัฐฯ (transit only), เวียดนาม" },
  { q: "พำนักได้นานเท่าไรต่อครั้ง?", a: "ส่วนใหญ่ 60-90 วันต่อครั้ง ขึ้นกับระเบียบของแต่ละ economy เช่น ออสเตรเลีย 90 วัน, ญี่ปุ่น 90 วัน, จีน 60 วัน, เกาหลีใต้ 90 วัน บัตร ABTC มีอายุ 5 ปีและเดินทางเข้าได้ไม่จำกัดจำนวนครั้งภายในช่วงอายุบัตร โดยไม่ต้องยื่นขอวีซ่าธุรกิจซ้ำ ผ่านช่อง APEC Lane ที่ด่านตรวจคนเข้าเมืองได้เลย ลดเวลาคิวเฉลี่ย 30-45 นาทีต่อเที่ยว" },
  { q: "ใครมีสิทธิ์ขอ?", a: "นักธุรกิจสัญชาติไทย ที่ดำรงตำแหน่งกรรมการ/ผู้บริหารระดับสูง หรือถือหุ้นบริษัทจดทะเบียน ≥ 5% และต้องเดินทางเชิงธุรกิจในภูมิภาค APEC" },
  { q: "ใช้เวลานานแค่ไหน?", a: "รวมประมาณ 4-6 เดือนนับจากยื่น (รวม pre-clearance ทุกประเทศ) — สหรัฐฯ และแคนาดาจะให้สิทธิ์เฉพาะ APEC Lane เท่านั้น ไม่ใช่วีซ่า" },
  { q: "ค่าธรรมเนียมราชการเท่าไร?", a: "ค่าธรรมเนียมภาครัฐประกอบด้วย: ค่ายื่นคำขอ ABTC ที่กรมการกงสุล กระทรวงการต่างประเทศ 5,000 บาทต่อบัตร (อายุ 5 ปี ไม่จำกัดจำนวนเที่ยว), ค่าธรรมเนียมตรวจประวัติอาชญากรรมที่สำนักงานตำรวจแห่งชาติ 100 บาท และค่าบริการของ NYC Legal แยกตามแพ็กเกจที่เลือก (ครอบคลุมการเตรียมเอกสาร แปลรับรอง และติดตามผลทุกขั้นตอน)" },
  { q: "ต่ออายุบัตรอย่างไร?", a: "ผู้ถือบัตรควรยื่นขอต่ออายุล่วงหน้า 3-6 เดือนก่อนวันหมดอายุที่กรมการกงสุล MFA โดยใช้ขั้นตอนเดียวกับการยื่นครั้งแรก คือเตรียมเอกสารบริษัท หลักฐานการเดินทางเชิงธุรกิจในช่วง 2 ปีที่ผ่านมา และผ่านการ pre-clearance ของแต่ละ economy ใหม่ทั้งหมด ทีม NYC จะแจ้งเตือนล่วงหน้าทาง LINE @nycli และเตรียมเอกสารให้พร้อมยื่นใน 7 วันทำการ" },
  { q: "หากเปลี่ยนงาน บัตรยังใช้ได้ไหม?", a: "บัตรยังคงใช้งานได้จนถึงวันหมดอายุที่ระบุ แต่ผู้ถือบัตรต้องยื่นแจ้งเปลี่ยนแปลงข้อมูลที่กรมการกงสุล MFA ภายใน 30 วันนับจากวันที่เปลี่ยนสังกัดบริษัทใหม่ พร้อมแนบหนังสือรับรองบริษัทใหม่และหนังสือลาออกจากบริษัทเดิม หากไม่แจ้งภายในกำหนด อาจถูกเพิกถอนสิทธิ์ APEC Lane โดยไม่ต้องแจ้งล่วงหน้า และไม่สามารถยื่นขอใหม่ได้ภายใน 6 เดือน" },
  { q: "บัตรหายต้องทำอย่างไร?", a: "ขั้นตอน: (1) แจ้งความที่สถานีตำรวจในพื้นที่เพื่อรับใบแจ้งความ (2) ยื่นขอบัตรใหม่ที่กรมการกงสุล MFA พร้อมใบแจ้งความ สำเนาบัตรเดิม และค่าธรรมเนียม 1,000 บาท (3) รอออกบัตรใหม่ ~3-4 สัปดาห์ ทีม NYC ดำเนินการแทนได้ใน 7 วันทำการ พร้อมประสาน economy ปลายทางแจ้งเปลี่ยนเลขบัตร" },
];

export const Route = createFileRoute("/apec/")({
  loader: async () => ({ clusterFaq: await buildClusterFaqJsonLdAsync("apec", "th", "https://nyclanguageinstitute.com/apec") }),
  head: ({ loaderData }: any) => ({
    meta: [
      { title: ctaTitle(TITLE, "/apec", "th")}, { name: "description", content: ctaDesc(DESC, "/apec", "th")},
      { name: "keywords", content: "APEC Card, ABTC, APEC Business Travel Card, บัตรนักธุรกิจ APEC, ขอ APEC Card, APEC Lane" },
      { property: "og:title", content: ctaTitle(TITLE, "/apec", "th")}, { property: "og:description", content: ctaDesc(DESC, "/apec", "th")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/apec" },
    ],
    links: canonicalAndHreflang("/apec", "/en/apec"),
    scripts: [
      ...buildLandingSchemas({
        path: "/apec", title: TITLE, description: DESC, serviceType: "APEC Business Travel Card Service", priceFromThb: 12000,
        steps, faqs,
        breadcrumbs: [{ name: "Home", path: "/" }, { name: "บริการ", path: "/services" }, { name: "APEC Card", path: "/apec" }],
      }),
      entitySameAsSchema(),
      softwareApplicationSchema({ url: `${SITE_AP}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing APEC Business Travel Card tooling." }),
      claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(`${SITE_AP}/apec`)),
    
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
  }),
  component: () => (
    <>
    <ServiceLanding
      badge="APEC Business Travel Card"
      title="APEC Card (ABTC) — เดินทาง 21 economies ฟรีวีซ่า"
      subtitle={DESC}
      quickAnswer={{
        q: "APEC Business Travel Card (ABTC) คืออะไรและใช้ได้กี่ประเทศ?",
        a: "APEC Card (ABTC) คือบัตรเดินทางสำหรับนักธุรกิจสัญชาติไทยที่ใช้แทนวีซ่าใน 21 economies เช่น ญี่ปุ่น เกาหลี สิงคโปร์ ออสเตรเลีย จีน ฮ่องกง — บัตรอายุ 5 ปี เดินทางไม่จำกัดจำนวนครั้ง พำนัก 60–90 วัน/ครั้ง ค่าธรรมเนียม MFA 5,000 บาท ค่าบริการ NYC เริ่ม 12,000 บาท",
      }}
      keyFacts={[
        { k: "ระยะเวลาดำเนินการ", v: "4–6 เดือน" },
        { k: "ค่าธรรมเนียม MFA", v: "5,000 บาท" },
        { k: "อายุบัตร", v: "5 ปี" },
        { k: "ใช้ได้", v: "21 economies APEC" },
      ]}
      heroBullets={[
        "เข้า-ออก 21 economies โดยไม่ต้องขอวีซ่าแยก",
        "ใช้ช่อง APEC Lane ที่สนามบินทั่วภูมิภาค",
        "บัตรอายุ 5 ปี เดินทางไม่จำกัดจำนวนครั้ง",
        "ทีม NYC ยื่นกรมการกงสุล MFA ครบขั้นตอน",
      ]}
      whoFor={{
        th: ["นักธุรกิจ/ผู้บริหารที่เดินทางใน APEC บ่อย", "ผู้ส่งออก-นำเข้าในภูมิภาคเอเชียแปซิฟิก", "Startup founders ที่ต้อง pitch ใน SG/HK/TW"],
        foreign: ["Foreign executives of Thai-registered companies meeting Thai-citizenship eligibility", "Dual-nationals using Thai passport for APEC travel"],
      }}
      steps={steps}
      pricing={[
        { label: "ค่าธรรมเนียมราชการ (MFA)", price: "5,000", note: "อายุบัตร 5 ปี" },
        { label: "ค่าบริการ NYC — Standard", price: "12,000", note: "ยื่น + ติดตามทุกขั้นตอน" },
        { label: "Premium (เร่งด่วน + รับ-ส่งเอกสาร)", price: "18,000", note: "พร้อมเลขาส่วนตัว" },
      ]}
      faqs={faqs}
      relatedLinks={[
        { to: "/visa", label: "วีซ่าทุกประเทศ" },
        { to: "/translate/mfa", label: "แปล + รับรอง MFA" },
        { to: "/police-clearance", label: "Police Clearance" },
        { to: "/embassy", label: "Embassy Legalization" },
        { to: "/notary", label: "Notary Public" },
        { to: "/blog/apec-business-travel-card-21-economies", label: "บทความ: APEC Card คืออะไร" },
      ]}
    />
    <RelatedHubGrid locale="th" />
    </>
  ),
});

